import { ColDef, SelectionChangedEvent } from 'ag-grid-community';
import { map } from 'rxjs';

import { Component, computed, effect, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { ApmTranslatePipe, AuthFacade, ResourcesService } from '@mitsubishi/core';
import { ButtonComponent, SectionTitleComponent } from '@mitsubishi/shared/ui';
import { TableComponent, TableRadioCellRendererComponent } from '@mitsubishi/ui/table';

import { ConfigurationRequest, HeatExchangerSelection, UomSystem } from '../../models/chiller-free-st.model';
import { ChillerFreeStService } from '../../services/chiller-free-st.service';
import { ChillerFreeStStore } from '../../stores/chiller-free-st.store';
import { CopCellRendererComponent } from '../cop-cell-renderer/cop-cell-renderer.component';

interface CompressorRow {
  compModelId: string;
  cop: number;
  specialMachineId: string | null;
  specialMachineDisplayName: string | null;
}

@Component({
  selector: 'apm-chiller-free-st-configuration-results',
  templateUrl: './chiller-free-st-configuration-results.component.html',
  styleUrl: './chiller-free-st-configuration-results.component.scss',
  imports: [SectionTitleComponent, ApmTranslatePipe, TableComponent, ButtonComponent],
})
export class ChillerFreeStConfigurationResultsComponent {
  readonly store = inject(ChillerFreeStStore);
  readonly resourcesService = inject(ResourcesService);
  #authFacade = inject(AuthFacade);
  #service = inject(ChillerFreeStService);
  #router = inject(Router);
  #route = inject(ActivatedRoute);

  readonly currentUsername = `${this.#authFacade.userProfile.firstName} ${this.#authFacade.userProfile.lastName}`;

  readonly selectedEvapId = signal<string | null>(null);
  readonly selectedCondId = signal<string | null>(null);
  readonly selectedComp = signal<CompressorRow | null>(null);

  readonly evaporatorRows = computed(() => {
    const results = this.store.combinationResults();
    const seen = new Set<string>();
    return results
      .filter(r => {
        if (seen.has(r.evapSelection.id)) return false;
        seen.add(r.evapSelection.id);
        return true;
      })
      .map(r => r.evapSelection);
  });

  readonly condenserRows = computed(() => {
    const evapId = this.selectedEvapId();
    if (!evapId) return [];
    const results = this.store.combinationResults().filter(r => r.evapSelection.id === evapId);
    const seen = new Set<string>();
    return results
      .filter(r => {
        if (seen.has(r.condSelection.id)) return false;
        seen.add(r.condSelection.id);
        return true;
      })
      .map(r => r.condSelection);
  });

  readonly compressorRows = computed<CompressorRow[]>(() => {
    const evapId = this.selectedEvapId();
    const condId = this.selectedCondId();
    if (!evapId || !condId) return [];

    this.store.uomSystem();

    const results = this.store
      .combinationResults()
      .filter(r => r.evapSelection.id === evapId && r.condSelection.id === condId);
    const seen = new Set<string>();
    return results
      .filter(r => {
        const key = r.specialMachineId ?? r.compModelPerfData.compModelId;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map(r => ({
        compModelId: r.compModelPerfData.compModelId,
        cop: r.compModelPerfData.cop,
        specialMachineId: r.specialMachineId,
        specialMachineDisplayName: r.specialMachineDisplayName,
      }));
  });

  readonly allSelected = computed(() => !!this.selectedEvapId() && !!this.selectedCondId() && !!this.selectedComp());

  readonly evaporatorColumnDefs: ColDef<HeatExchangerSelection>[] = [
    {
      headerName: '',
      cellRenderer: TableRadioCellRendererComponent,
      width: 50,
      maxWidth: 50,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMenu: true,
      cellStyle: { display: 'flex', 'align-items': 'center', 'justify-content': 'center' },
    },
    {
      field: 'tubeDiaCode',
      headerName: this.resourcesService.getTextResource(456935),
    },
    {
      field: 'tubeLen',
      headerName: this.resourcesService.getTextResource(456936),
    },
    {
      field: 'pipeNumType',
      headerName: this.resourcesService.getTextResource(456937),
    },
    {
      field: 'pipeDiaType',
      headerName: this.resourcesService.getTextResource(456938),
    },
  ];

  readonly condenserColumnDefs: ColDef<HeatExchangerSelection>[] = [
    {
      headerName: '',
      cellRenderer: TableRadioCellRendererComponent,
      width: 50,
      maxWidth: 50,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMenu: true,
      cellStyle: { display: 'flex', 'align-items': 'center', 'justify-content': 'center' },
    },
    {
      field: 'tubeDiaCode',
      headerName: this.resourcesService.getTextResource(456935),
    },
    {
      field: 'tubeLen',
      headerName: this.resourcesService.getTextResource(456936),
    },
    {
      field: 'pipeNumType',
      headerName: this.resourcesService.getTextResource(456937),
    },
    {
      field: 'pipeDiaType',
      headerName: this.resourcesService.getTextResource(456938),
    },
  ];

  readonly compressorColumnDefs: ColDef[] = [
    {
      headerName: '',
      cellRenderer: TableRadioCellRendererComponent,
      width: 50,
      maxWidth: 50,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMenu: true,
      cellStyle: { display: 'flex', 'align-items': 'center', 'justify-content': 'center' },
    },
    {
      field: 'compModelId',
      headerName: this.resourcesService.getTextResource(459036),
    },
    {
      field: 'cop',
      headerName: this.resourcesService.getTextResource(457031),
      cellRenderer: CopCellRendererComponent,
      cellRendererParams: {
        getUomSystem: () => this.store.uomSystem(),
        getMapping: () => (this.store.fieldMappingMap()['COOLING'] ?? {})['machineInfo.cop'],
        computeFn: (value: number, sourceUom: UomSystem) =>
          this.#service
            .compute({ uomSystem: sourceUom, computations: [{ field: 'cop', inputs: { value } }] })
            .pipe(map(res => res.results['cop'])),
      },
    },
    {
      field: 'specialMachineDisplayName',
      headerName: this.resourcesService.getTextResource(456934),
    },
  ];

  constructor() {
    effect(() => {
      this.store.combinationResults();
      this.selectedEvapId.set(null);
      this.selectedCondId.set(null);
      this.selectedComp.set(null);
    });
  }

  onEvapSelected(event: SelectionChangedEvent): void {
    const rows = event.api.getSelectedRows();
    this.selectedEvapId.set(rows[0]?.id ?? null);
    this.selectedCondId.set(null);
    this.selectedComp.set(null);
  }

  onCondSelected(event: SelectionChangedEvent): void {
    const rows = event.api.getSelectedRows();
    this.selectedCondId.set(rows[0]?.id ?? null);
    this.selectedComp.set(null);
  }

  onCompSelected(event: SelectionChangedEvent): void {
    const rows = event.api.getSelectedRows();
    this.selectedComp.set(rows[0] ?? null);
  }

  generateDatasheet(): void {
    const comp = this.selectedComp()!;
    const request: ConfigurationRequest = {
      evapId: this.selectedEvapId()!,
      condId: this.selectedCondId()!,
      compModelId: comp.compModelId,
      specialMachineId: comp.specialMachineId,
      specialMachineDisplayName: comp.specialMachineDisplayName,
      userName: this.currentUsername,
      userDataInput: this.store.lastCombRequest()!,
      uomSystem: this.store.uomSystem(),
    };

    this.#service.saveConfiguration(request).subscribe(response => {
      const urlTree = this.#router.createUrlTree(['../data-sheet', response.uuid], { relativeTo: this.#route });
      window.open(`#/${this.#router.serializeUrl(urlTree)}`, '_blank');
    });
  }
}
